import { Box, Container, Typography, Button, Stack } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import AnimatedSection from './AnimatedSection';
import { usePageContent } from '../../hooks/usePublicApi';
import { getImageUrl } from '../../utils/imageUrl';

export default function CTABanner({
  title,
  subtitle,
  primaryAction,
  secondaryAction,
  dark = true,
  pageKey = 'cta', // Which page's CTA to load (home, about, karriere, etc.)
}) {
  const { content: pageContent } = usePageContent(pageKey);

  const bannerData = pageContent?.cta || {};
  const displayTitle = title || bannerData?.title || 'Bereit für Ihr Projekt?';
  const displaySubtitle = subtitle || bannerData?.subtitle || 'Kontaktieren Sie uns für eine kostenlose Beratung und ein unverbindliches Angebot.';
  const displayPrimaryAction = primaryAction || {
    label: bannerData?.buttonText || bannerData?.primaryAction?.label || 'Anfrage senden',
    to: bannerData?.buttonLink || bannerData?.primaryAction?.to || '/anfrage',
  };

  const backgroundImage = bannerData?.backgroundImage
    ? getImageUrl(bannerData.backgroundImage.filename, bannerData.backgroundImage.folder)
    : null;
  const overlayOpacity = bannerData?.overlayOpacity ?? 0.75;

  return (
    <Box
      sx={{
        py: { xs: 10, md: 12 },
        bgcolor: dark ? 'secondary.main' : 'background.light',
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      {/* Background Image */}
      {backgroundImage && (
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            backgroundImage: `url(${backgroundImage})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            filter: `brightness(${1 - overlayOpacity * 0.3})`,
            '&::after': {
              content: '""',
              position: 'absolute',
              inset: 0,
              background: `linear-gradient(135deg, rgba(15,29,46,${Math.min(overlayOpacity + 0.1, 1)}) 0%, rgba(15,29,46,${Math.max(overlayOpacity - 0.05, 0)}) 50%, rgba(15,29,46,${overlayOpacity}) 100%)`,
            },
          }}
        />
      )}

      {/* Animated gradient background for non-image banners */}
      {dark && !backgroundImage && (
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            background: 'radial-gradient(circle at 50% 50%, rgba(209, 188, 138, 0.08) 0%, transparent 60%)',
            opacity: 0.4,
          }}
        />
      )}

      {/* Modern accent line with gradient */}
      <Box
        sx={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          height: 2,
          background: 'linear-gradient(90deg, transparent 0%, #D1BC8A 30%, #E8DEC3 50%, #D1BC8A 70%, transparent 100%)',
          opacity: 0.7,
        }}
      />

      <Container maxWidth="md" sx={{ position: 'relative', textAlign: 'center' }}>
        <AnimatedSection>
          <Box>
            <Typography
              variant="h2"
              sx={{
                color: dark ? 'white' : 'text.primary',
                mb: 3,
                background: dark
                  ? 'linear-gradient(135deg, #FFFFFF 0%, rgba(255,255,255,0.9) 100%)'
                  : 'none',
                WebkitBackgroundClip: dark ? 'text' : 'initial',
                WebkitTextFillColor: dark ? 'transparent' : 'initial',
                backgroundClip: dark ? 'text' : 'initial',
                textShadow: dark ? 'none' : '0 2px 8px rgba(0, 0, 0, 0.1)',
              }}
            >
              {displayTitle}
            </Typography>
            <Typography
              variant="subtitle1"
              sx={{
                color: dark ? 'rgba(255,255,255,0.85)' : 'text.secondary',
                mb: 5,
                maxWidth: 560,
                mx: 'auto',
                fontSize: '1.1rem',
                lineHeight: 1.7,
              }}
            >
              {displaySubtitle}
            </Typography>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} justifyContent="center">
              <Button
                variant="contained"
                color="primary"
                size="large"
                component={RouterLink}
                to={displayPrimaryAction.to}
              >
                {displayPrimaryAction.label}
              </Button>
              {secondaryAction && (
                <Button
                  variant={dark ? 'outlined' : 'contained'}
                  color={dark ? 'primary' : 'secondary'}
                  size="large"
                  component={RouterLink}
                  to={secondaryAction.to}
                >
                  {secondaryAction.label}
                </Button>
              )}
            </Stack>
          </Box>
        </AnimatedSection>
      </Container>
    </Box>
  );
}
